import { getFeatureFlags } from '../../core/featureFlags';
import type { AutomationFeatureFlags } from '../../core/featureFlags';
import { AUTOMATION_PROMPT_DEFAULTS } from '../../../server/src/modules/operations/automationPrompts.js';
import { newId } from '../../core/ids';
import { applyRoleContextBudget } from '../../core/roleContext';
import type { ProviderAdapter } from '../../core/providers/types';
import type { ChatMessage, ModelSettings, Preset } from '../../core/types';
import { getEntitlements } from '../subscription/entitlements';
import { applyControlToolCalls, buildRoleTools, completedToolCalls, mergeToolCallDelta, parseRichOutput } from './richOutput';
import type { StreamToolCall } from './richOutput';
import { ttsPolicyInstructions } from './ttsPolicy';

export interface RoleVoiceDecisionOptions {
  preset: Preset;
  message: ChatMessage;
  context: readonly ChatMessage[];
  adapter: ProviderAdapter;
  model: string;
  settings: ModelSettings;
  features?: AutomationFeatureFlags;
  signal?: AbortSignal;
}

/** Ask once after the reply is complete; only an explicit voice instruction authorizes synthesis. */
export async function decideRoleVoice(options: RoleVoiceDecisionOptions): Promise<boolean> {
  const { preset, message, context, adapter, model, settings, signal } = options;
  const entitlements = getEntitlements();
  if (signal?.aborted || message.role !== 'assistant' || message.status !== 'done'
    || message.content.trim() === '' || !preset.autoTtsEnabled || !entitlements.active || !entitlements.capabilities.autoTts) return false;

  const features = options.features ?? getFeatureFlags();
  const instruction = [
    (features.automationPrompts ?? AUTOMATION_PROMPT_DEFAULTS).voiceDecision,
    ttsPolicyInstructions(preset, features.ttsInitiative),
  ].filter(Boolean).join('\n');
  if (instruction.trim() === '') return false;
  const decisionMessage: ChatMessage = {
    id: newId(), conversationId: message.conversationId, role: 'user', content: instruction,
    createdAt: Date.now(), status: 'done',
  };
  let text = '';
  const calls = new Map<number, StreamToolCall>();
  try {
    for await (const chunk of adapter.chat(applyRoleContextBudget({
      messages: [...context.map(item => ({ ...item })), decisionMessage],
      settings: { ...settings }, model,
      tools: buildRoleTools(preset, { autoText: false, autoImage: false, autoTts: true }, features),
      ...(signal ? { signal } : {}),
    }, preset))) {
      if (signal?.aborted) return false;
      if (chunk.type === 'text') text += chunk.delta ?? '';
      else if (chunk.type === 'tool_call') mergeToolCallDelta(calls, chunk.delta ?? '');
      else if (chunk.type === 'error') throw new Error(chunk.error ?? '语音发送决策失败');
      else if (chunk.type === 'done') break;
    }
  } catch (error) {
    if (signal?.aborted) return false;
    throw error;
  }
  if (signal?.aborted) return false;
  return applyControlToolCalls(parseRichOutput(text), completedToolCalls(calls)).ttsRequested;
}
